import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StatusScreen } from '../../../../components';
import { useBackdropContext } from '../../../../context/backdrop';
import { useInstallationContext } from '../../../../context/installation';
import { useRefetchData } from '../../../../hooks';
import { useInvitationsOperations } from '../../../../hooks/invitations/use-invitations-operations';
import { useNotificationsOperations } from '../../../../hooks/notifications/use-notifications-operations';
import { ROUTES } from '../../../../routes';
import { NotificationInstallationInvitation, StatusTypeEnum } from '../../../../types';
import { pathnameWithParameters } from '../../../../utils/location';
import InvitationReceivedConfirmation from './confirmation';

type InvitationReceivedProps = {
  notificationData: NotificationInstallationInvitation;
};

const InvitationReceived: React.FC<InvitationReceivedProps> = ({ notificationData }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation('invitation');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const { correlationId } = useParams<{ correlationId: string }>();
  const { markAsHidden } = useNotificationsOperations();
  const { acceptInvitation, rejectInvitation } = useInvitationsOperations();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { selectInstallation } = useInstallationContext();
  const { refetchInstallations } = useRefetchData();

  useEffect(() => {
    const confirmation = new URLSearchParams(search).get('confirmation');
    setShowConfirmation(!!confirmation);
  }, [search]);

  const onAccept = () => {
    if (!correlationId) return;

    acceptInvitation(notificationData.invitationId, () => {
      refetchInstallations();

      markAsHidden(correlationId, {
        onSuccess: () => {
          const installationId = notificationData.params.find((x) => x.name === 'InstallationId')?.value;

          if (installationId) {
            selectInstallation(installationId, () => {
              turnOffBackdrop();

              navigate(
                pathnameWithParameters(pathname, search, {
                  key: 'confirmation',
                  value: true,
                }),
              );
            });
          } else {
            window.location.href = ROUTES.Root();
          }
        },
        onError: () => {
          turnOffBackdrop();
        },
      });
    });
  };

  const onReject = () => {
    if (!correlationId) return;

    turnOnBackdrop();
    rejectInvitation(notificationData.invitationId, () => {
      markAsHidden(correlationId, {
        onSuccess: () => {
          turnOffBackdrop();
          navigate(-1);
        },
      });
    });
  };

  if (showConfirmation) return <InvitationReceivedConfirmation />;

  return (
    <StatusScreen
      header={t('received.title')}
      subTitle={t('received.message', {
        creatorName: notificationData.userName,
        installationName: notificationData.installationName,
      })}
      note={t('received.expirationMessage')}
      type={StatusTypeEnum.JOIN}
      secondaryBackground
      onPrimaryBtnClick={onAccept}
      primaryBtnText={t('received.acceptInvitation')}
      onSecondaryBtnClick={onReject}
      secondaryBtnText={t('received.rejectInvitation')}
    />
  );
};

export default InvitationReceived;
